import { Helmet } from "react-helmet-async";
import { Header } from "@/components/Header";
import { Footer } from "@/components/Footer";
import { Breadcrumb } from "@/components/Breadcrumb";

export default function TermsAndConditions() {
  return (
    <>
      <Helmet>
        <title>Terms and Conditions | Masonry Pro</title>
        <meta name="description" content="Terms and conditions for Masonry Pro Construction. Please review the terms governing your use of our website and masonry services across North Jersey." />
        <meta property="og:description" content="Terms and conditions for Masonry Pro Construction. Please review the terms governing your use of our website and masonry services across North Jersey." />
        <meta property="og:title" content="Terms and Conditions | Masonry Pro" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        <main className="py-16 md:py-20">
          <div className="container mx-auto px-4 max-w-4xl">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Terms and Conditions</h1>
            <p className="text-muted-foreground mb-10">Effective Date: February 11, 2026</p>

            <div className="prose prose-lg max-w-none text-muted-foreground space-y-6">
              <p>
                These Terms and Conditions ("Terms") govern your use of the website operated by Masonry Pro Construction ("Company," "we," "us," or "our"), located at masonry-pro.com (the "Site"). By accessing or using the Site, you agree to be bound by these Terms. If you do not agree with any part of these Terms, you must not use the Site.
              </p>

              <h2 className="text-2xl font-bold text-foreground">1. Acceptance of Terms</h2>
              <p>
                By accessing and using this Site, you acknowledge that you have read, understood, and agree to be bound by these Terms, as well as our Privacy Policy, which is incorporated herein by reference. We reserve the right to update or modify these Terms at any time without prior notice. Your continued use of the Site after any modifications constitutes acceptance of the revised Terms.
              </p>

              <h2 className="text-2xl font-bold text-foreground">2. Description of Services</h2>
              <p>
                Masonry Pro Construction is a licensed masonry, concrete, paving, and waterproofing contractor serving Northern New Jersey, including Passaic, Bergen, Essex, and Morris Counties. Our services include, but are not limited to:
              </p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Stone masonry installation and repair</li>
                <li>Retaining wall installation and repair</li>
                <li>Stone veneer installation</li>
                <li>Masonry steps installation</li>
                <li>Outdoor kitchen installation</li>
                <li>Brick work and block work</li>
                <li>Paver patio installation and repair</li>
                <li>Paver walkway installation</li>
                <li>Concrete driveway and sidewalk installation</li>
                <li>Stamped concrete installation</li>
                <li>Concrete foundations and footings</li>
                <li>Basement and patio waterproofing</li>
              </ul>
              <p>
                The information provided on this Site is for general informational purposes only. It is not a substitute for a professional on-site assessment. Actual project scope, materials, timelines, and costs will be determined through a formal estimate and written agreement.
              </p>

              <h2 className="text-2xl font-bold text-foreground">3. Estimates and Proposals</h2>
              <p>
                Any estimate or proposal provided through our Site, by phone, by email, or in person is an approximation based on the information available at the time. Estimates are not binding contracts. Final pricing, scope of work, materials, and timelines will be set forth in a separate written service agreement or contract. Estimates are generally valid for 30 days from the date issued, unless otherwise stated in writing.
              </p>

              <h2 className="text-2xl font-bold text-foreground">4. Service Agreements and Contracts</h2>
              <p>
                All masonry, concrete, paving, and waterproofing work performed by us will be governed by a separate written service agreement or contract. In the event of a conflict between these Terms and a signed service agreement, the terms of the signed service agreement shall control.
              </p>

              <h2 className="text-2xl font-bold text-foreground">5. Payment Terms</h2>
              <p>Payment terms for services are outlined in the individual service agreement for each project. General payment policies include:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>A deposit may be required before work begins, as specified in your service agreement.</li>
                <li>Progress payments may be required at specified milestones during the project.</li>
                <li>Final payment is due upon completion of the project and your satisfaction with the work.</li>
                <li>Accepted payment methods will be specified in your service agreement.</li>
              </ul>
              <p>
                Late payments may be subject to interest charges as permitted under New Jersey law and as specified in the service agreement.
              </p>

              <h2 className="text-2xl font-bold text-foreground">6. Licensing and Insurance</h2>
              <p>
                Masonry Pro Construction is a licensed contractor in the State of New Jersey. We maintain appropriate insurance coverage, including general liability insurance and workers' compensation insurance, as required by New Jersey law. Proof of licensing and insurance is available upon request.
              </p>

              <h2 className="text-2xl font-bold text-foreground">7. New Jersey Home Improvement Contractor Act Compliance</h2>
              <p>
                As a home improvement contractor operating in New Jersey, we comply with the New Jersey Contractor's Registration Act (N.J.S.A. 56:8-136 et seq.) and the Consumer Fraud Act (N.J.S.A. 56:8-1 et seq.).
              </p>

              <h2 className="text-2xl font-bold text-foreground">8. Warranties and Disclaimers</h2>

              <h3 className="text-xl font-semibold text-foreground">8.1 Workmanship Warranty</h3>
              <p>
                Specific warranty terms for our workmanship are provided in the individual service agreement for each project.
              </p>

              <h3 className="text-xl font-semibold text-foreground">8.2 Material Warranties</h3>
              <p>
                Warranties on materials (such as pavers, stone, concrete products, and waterproofing membranes) are provided by the respective manufacturers and are separate from our workmanship warranty.
              </p>

              <h3 className="text-xl font-semibold text-foreground">8.3 Limitations</h3>
              <p>Warranties do not cover:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>Damage caused by acts of nature, including floods, earthquakes, hurricanes, or unusually severe weather events</li>
                <li>Damage resulting from improper use, abuse, neglect, or failure to perform reasonable maintenance</li>
                <li>Normal wear and tear, including minor surface cracking in concrete</li>
                <li>Damage caused by third parties, including other contractors or unauthorized modifications</li>
                <li>Issues arising from pre-existing conditions not identified in the original scope of work</li>
                <li>Changes in appearance due to natural aging, weathering, or patina development</li>
                <li>Settling or movement caused by soil conditions not apparent at the time of installation</li>
              </ul>

              <h3 className="text-xl font-semibold text-foreground">8.4 Website Disclaimer</h3>
              <p>
                THE INFORMATION ON THIS SITE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED. WE DISCLAIM ALL WARRANTIES, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.
              </p>

              <h2 className="text-2xl font-bold text-foreground">9. Limitation of Liability</h2>
              <p>
                TO THE FULLEST EXTENT PERMITTED BY NEW JERSEY LAW, MASONRY PRO CONSTRUCTION, ITS OWNERS, OFFICERS, EMPLOYEES, AND AGENTS SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING OUT OF OR RELATED TO YOUR USE OF THE SITE OR ANY INFORMATION PROVIDED ON THE SITE.
              </p>

              <h2 className="text-2xl font-bold text-foreground">10. Intellectual Property</h2>
              <p>
                All content on this Site, including but not limited to text, graphics, logos, images, photographs of completed projects, videos, page layouts, and software, is the property of Masonry Pro Construction or its content providers and is protected by United States and international copyright, trademark, and other intellectual property laws.
              </p>

              <h2 className="text-2xl font-bold text-foreground">11. User Submissions</h2>
              <p>
                If you submit any content to us through the Site (such as photos, testimonials, reviews, or project descriptions), you grant us a non-exclusive, royalty-free, perpetual, irrevocable, and fully sublicensable right to use, reproduce, modify, adapt, publish, translate, distribute, and display such content in connection with our business.
              </p>

              <h2 className="text-2xl font-bold text-foreground">12. Prohibited Uses</h2>
              <p>You agree not to use the Site for any of the following purposes:</p>
              <ul className="list-disc pl-6 space-y-1">
                <li>In any way that violates any applicable federal, state, or local law or regulation</li>
                <li>To transmit any advertising or promotional material without our prior written consent</li>
                <li>To impersonate or attempt to impersonate the Company or any other person or entity</li>
                <li>To engage in any conduct that restricts or inhibits anyone's use or enjoyment of the Site</li>
                <li>To introduce any viruses, Trojan horses, worms, or other harmful material</li>
                <li>To attempt to gain unauthorized access to any portion of the Site</li>
                <li>To scrape, mine, or otherwise collect information from the Site for unauthorized purposes</li>
              </ul>

              <h2 className="text-2xl font-bold text-foreground">13. Indemnification</h2>
              <p>
                You agree to indemnify, defend, and hold harmless Masonry Pro Construction from and against any and all claims, damages, losses, liabilities, costs, and expenses arising out of or related to your use of the Site, your violation of these Terms, or your violation of any rights of another party.
              </p>

              <h2 className="text-2xl font-bold text-foreground">14. Dispute Resolution</h2>

              <h3 className="text-xl font-semibold text-foreground">14.1 Governing Law</h3>
              <p>
                These Terms shall be governed by and construed in accordance with the laws of the State of New Jersey, without regard to its conflict of law provisions.
              </p>

              <h3 className="text-xl font-semibold text-foreground">14.2 Informal Resolution</h3>
              <p>
                Before filing any formal legal action, you agree to first attempt to resolve any dispute with us informally by contacting us. We will attempt to resolve the dispute informally within 30 days of receiving your notice.
              </p>

              <h3 className="text-xl font-semibold text-foreground">14.3 Jurisdiction and Venue</h3>
              <p>
                Any legal action or proceeding shall be brought exclusively in the state or federal courts located in Passaic County, New Jersey.
              </p>

              <h3 className="text-xl font-semibold text-foreground">14.4 Construction-Related Disputes</h3>
              <p>
                Disputes related to the quality of work, project scope, or service agreements are governed by the terms of the individual service agreement for that project.
              </p>

              <h2 className="text-2xl font-bold text-foreground">15. Force Majeure</h2>
              <p>
                We shall not be liable for any failure or delay in performance resulting from causes beyond our reasonable control, including but not limited to acts of God, natural disasters, pandemics, government actions, labor disputes, supply chain disruptions, or severe weather.
              </p>

              <h2 className="text-2xl font-bold text-foreground">16. Severability</h2>
              <p>
                If any provision of these Terms is found to be invalid, illegal, or unenforceable, the remaining provisions shall continue in full force and effect.
              </p>

              <h2 className="text-2xl font-bold text-foreground">17. Entire Agreement</h2>
              <p>
                These Terms, together with our Privacy Policy and any signed service agreement, constitute the entire agreement between you and Masonry Pro Construction with respect to your use of the Site.
              </p>

              <h2 className="text-2xl font-bold text-foreground">18. Waiver</h2>
              <p>
                Our failure to enforce any right or provision of these Terms shall not constitute a waiver of that right or provision.
              </p>

              <h2 className="text-2xl font-bold text-foreground">19. Accessibility</h2>
              <p>
                We are committed to making our Site accessible to all users, including individuals with disabilities. We strive to comply with the Web Content Accessibility Guidelines (WCAG) 2.1 Level AA standards. If you experience any difficulty accessing any part of our Site, please contact us.
              </p>

              <h2 className="text-2xl font-bold text-foreground">20. Contact Us</h2>
              <p>If you have questions about these Terms and Conditions, please contact us:</p>
              <p>
                <strong>Masonry Pro Construction</strong><br />
                36 Highview Ave, Totowa, NJ 07512<br />
                Email: <a href="mailto:masonryproconstruction@aol.com" className="text-secondary hover:underline">masonryproconstruction@aol.com</a><br />
                Phone: <a href="tel:+19737252395" className="text-secondary hover:underline">(973) 725-2395</a>
              </p>
            </div>
          </div>
        </main>
        <Footer />
      </div>
    </>
  );
}
